/**
 * Visible explanation for rules controls the user's role cannot use (#847).
 *
 * `GatedButton` is the control: when its gate is closed it stays in the tab
 * order (`aria-disabled`, not native `disabled`, with its click guarded) and
 * carries the reason three ways -- a tooltip on its wrapper (a disabled button
 * may not show its own `title`), a visually-hidden description it points at
 * with `aria-describedby`, and (for icon buttons) its accessible name.
 * `RulesPermissionNotice` is the always-visible summary of what the user can't
 * do and which permission unlocks it, so touch and screen-reader users get the
 * same information as a mouse user.
 *
 * Unlike ContextualSideNav / KnowledgeBasePanel / the purge button in
 * ProjectSetup, which hide what a user may not use, these controls are shown
 * disabled with the reason: a steward who can see Run All Rules but is told it
 * needs rules:execute knows what to ask for; one who never sees it does not
 * know the feature exists.
 */

import { Lock } from "lucide-react";
import { useId, type ReactNode } from "react";

import {
  missingRulesPermissions,
  type ActionGate,
  type RulesGates,
} from "../../lib/rulesPermissions";

/**
 * A button gated on a permission.
 *
 * - `gate` closed: `aria-disabled="true"`, focusable, click does nothing, and
 *   the reason is announced (aria-describedby) and shown on hover.
 * - `disabled` is for every OTHER reason the control is off (busy, nothing to
 *   run); it is the native attribute, and applies only while the gate is open.
 */
export function GatedButton({
  gate,
  disabled = false,
  onClick,
  className,
  ariaLabel,
  title,
  type = "button",
  children,
}: {
  gate: ActionGate;
  disabled?: boolean;
  onClick?: () => void;
  className: string;
  ariaLabel?: string;
  /** Hover tooltip naming the action while it is allowed (icon buttons); a blocked control shows the reason instead. */
  title?: string;
  type?: "button" | "submit";
  children: ReactNode;
}) {
  const reasonId = useId();
  const blocked = !gate.allowed;
  return (
    <span
      className={blocked ? "inline-flex cursor-not-allowed" : "inline-flex"}
      title={gate.reason ?? undefined}
      data-gated={blocked ? "true" : undefined}
    >
      <button
        type={type}
        onClick={(event) => {
          if (blocked) {
            event.preventDefault(); // a blocked submit button must not submit its form
            return;
          }
          onClick?.();
        }}
        disabled={!blocked && disabled}
        aria-disabled={blocked ? true : undefined}
        aria-describedby={blocked ? reasonId : undefined}
        aria-label={ariaLabel}
        title={blocked ? undefined : title}
        className={`${className} aria-disabled:opacity-40 aria-disabled:pointer-events-none`}
      >
        {children}
      </button>
      {blocked && (
        <span id={reasonId} className="sr-only">
          {gate.reason}
        </span>
      )}
    </span>
  );
}

const ACTION_TEXT: Record<string, string> = {
  "rules:execute": "run rules or describe them in English",
  "rules:create": "add rules",
  "rules:update": "edit rules or submit them for review",
  "rules:approve": "approve or reject rules",
  "rules:delete": "retire rules",
};

export function RulesPermissionNotice({ gates }: { gates: RulesGates }) {
  const missing = missingRulesPermissions(gates);
  if (missing.length === 0) return null;
  return (
    <div
      role="note"
      className="rounded-xl border border-[var(--warning)]/30 bg-[var(--warning)]/10 p-3 text-xs text-[var(--warning)] flex items-start gap-2"
    >
      <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
      <div className="space-y-0.5">
        <div className="font-medium">
          Some rule actions are unavailable to your role.
        </div>
        <ul className="list-disc list-inside">
          {missing.map((permission) => (
            <li key={permission}>
              To {ACTION_TEXT[permission] ?? "do this"}:{" "}
              <code className="font-mono">{permission}</code>
            </li>
          ))}
        </ul>
        <div className="text-[var(--muted-foreground)]">
          Rule permissions come from your rule role on this project (DQ Analyst
          or Data Steward). Ask the project owner or a tenant administrator to
          assign one (Project Setup, Team Members).
        </div>
      </div>
    </div>
  );
}
